import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

const AuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const auth = useAuth();
    const [hasProcessed, setHasProcessed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Prevent multiple processing attempts
        if (hasProcessed) return;

        const handleCallback = async () => {
            try {
                // Wait for the OIDC library to process the callback
                if (auth.isLoading) {
                    return; // Still processing
                }

                setHasProcessed(true);

                if (auth.error) {
                    console.error('Authentication error:', auth.error);
                    setError(auth.error.message || 'Authentication failed');
                    
                    // Wait a bit before redirecting to show error
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 3000);
                    return;
                }

                if (auth.isAuthenticated) {
                    // Redirect to intended path or home
                    const intendedPath = sessionStorage.getItem('intendedPath') || '/';
                    sessionStorage.removeItem('intendedPath');
                    navigate(intendedPath, { replace: true });
                } else {
                    // If not authenticated and not loading, wait a bit then redirect
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 2000);
                }
            } catch (error) {
                console.error('Error handling auth callback:', error);
                setHasProcessed(true);
                setError('Failed to process authentication callback');
                
                setTimeout(() => {
                    navigate('/', { replace: true });
                }, 3000);
            }
        };

        // Add a small delay to ensure OIDC processing is complete
        const timer = setTimeout(handleCallback, 100);
        
        return () => clearTimeout(timer);
    }, [auth.isLoading, auth.error, auth.isAuthenticated, navigate, hasProcessed]);

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
        }}>
            <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
                {error ? (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
                        <h2 style={{ marginBottom: '16px' }}>Authentication Failed</h2>
                        <p style={{ marginBottom: '20px', opacity: 0.9 }}>{error}</p>
                        <p style={{ fontSize: '14px', opacity: 0.7 }}>Redirecting to home page...</p>
                    </>
                ) : (
                    <>
                        <div style={{
                            width: '60px',
                            height: '60px',
                            border: '4px solid rgba(255,255,255,0.3)',
                            borderTop: '4px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 20px'
                        }}></div>
                        <p style={{ fontSize: '18px', margin: 0 }}>
                            {auth.isAuthenticated ? 'Sign in successful! Redirecting...' : 'Completing sign in...'}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthCallback;